import { useDisclosure } from '@mantine/hooks';
import { useEffect } from 'react';
import { useUpdateEffect } from 'react-use';
import i18n from '../../base-dictionary';
import { BrandedLoadingOverlay } from '../../components/loader/branded-loader';
import { PageLayout } from '../../components/page-layout/page-layout';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useStrategyStore } from '../../store/strategy-store/strategy-store';
import { setActivePanel, setSelectedInitiative } from '../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../store/strategy-store/strategy-store.type';
import { baseWhite } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Icon } from '../../ui-library/icon/icon';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { toast } from '../notifications';
import { Filters } from './initiative-list/filters/filters';
import { MainSection } from './initiative-list/main-section/main-section';
import { Overview } from './initiative-list/overview/overview';
import { SideSection } from './initiative-list/side-section/side-section';
import { InitiativeModal } from './initiative-modal/initiative-modal';
import { InitiativeModalMode } from './initiative-modal/initiative-modal.type';
import { useCreateInitiativeMutation, useStrategyData } from './strategy-v2.hooks';

export const StrategyV2 = () => {
  const { initiativesQuery, kpiOverviewDataQuery } = useStrategyData();

  const initiatives = useStrategyStore((state) => state.initiatives);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activePanel = useStrategyStore((state) => state.activePanel);

  const [opened, { open, close }] = useDisclosure(false);

  useEffect(() => {
    return () => {
      setActivePanel(Panel.Filters);
    };
  }, []);

  useUpdateEffect(() => {
    if (portfolio) {
      setActivePanel(Panel.Filters);
    }
  }, [portfolio]);

  const createInitiativeMutation = useCreateInitiativeMutation(portfolio, {
    onSuccess: (data) => {
      initiativesQuery.refetch();
      kpiOverviewDataQuery.refetch();
      close();

      if (data.message) {
        toast.warning(data.message);
      } else {
        toast.success(i18n.t('strategy.initiatives.created_success', { name: data.name }));
      }
    },
    onError: (error) => {
      toast.error(
        i18n.t('strategy.initiatives.create_error', {
          error: error instanceof Error ? error.message : i18n.t('strategy.initiatives.unknown_error'),
        }),
      );
    },
  });

  /*
   * @param show - Whether to show the filters
   * @description Handles the show state of the filters
   */
  function handleSetShow(show: boolean): void {
    setActivePanel(show ? Panel.Filters : null);
    if (show) {
      setSelectedInitiative(null);
    }
  }

  return (
    <MenuDrivenContainer
      header={i18n.t('navigation.strategy')}
      actions={
        <Button
          leftSection={<Icon name="add_circle" size={16} color={baseWhite} />}
          variant="primary"
          radius="xl"
          onClick={open}
          size="sm"
        >
          {i18n.t('strategy.initiatives.add_new')}
        </Button>
      }
    >
      <BrandedLoadingOverlay
        visible={initiativesQuery.isFetching || kpiOverviewDataQuery.isFetching}
        variant="colored"
        transitionDuration={30}
      />
      <PageLayout
        controlBar={null}
        overview={{
          title: i18n.t('strategy.overview.title'),
          content: initiatives.length > 0 ? <Overview /> : null,
          collapsible: initiatives.length > 0,
          isEmpty: initiatives.length === 0,
          emptyLabel: i18n.t('strategy.initiatives.no_initiatives'),
        }}
        sideFilters={{
          title: i18n.t('common.filters'),
          content: <Filters setShow={handleSetShow} />,
          show: activePanel === Panel.Filters,
          setShow: handleSetShow,
        }}
        mainSection={<MainSection isLoading={initiativesQuery.isFetching || kpiOverviewDataQuery.isFetching} />}
        sideSection={<SideSection />}
        showSideSection={activePanel === Panel.Side || activePanel === Panel.OtherInitiatives}
        data-page-layout
      />
      <InitiativeModal
        mode={InitiativeModalMode.Create}
        opened={opened}
        onClose={close}
        onSubmit={createInitiativeMutation.mutateAsync}
        isSubmitting={createInitiativeMutation.isPending}
      />
    </MenuDrivenContainer>
  );
};
